import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import documentApi, { DocumentItem } from '../../services/documentApi';

// Map entity types from ComparisonTable to PATCH endpoint field names
const ENTITY_TO_FIELD: Record<string, string> = {
    'invoice_id': 'invoice_number',
    'invoice_type': 'invoice_type',
    'invoice_date': 'invoice_date',
    'due_date': 'due_date',
    'supplier_name': 'vendor_name',
    'supplier_address': 'supplier_address',
    'supplier_tax_id': 'supplier_tax_id',
    'supplier_email': 'supplier_email',
    'supplier_phone': 'supplier_phone',
    'supplier_website': 'supplier_website',
    'supplier_iban': 'supplier_iban',
    'receiver_name': 'receiver_name',
    'receiver_address': 'receiver_address',
    'total_amount': 'total_amount',
    'net_amount': 'net_amount',
    'total_tax_amount': 'total_tax_amount',
    'currency': 'currency',
};
import './ComparisonPage.scss';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Components
import ComparisonTable from './components/ComparisonTable';
import DocumentAiPdfViewer from './components/DocumentAiPdfViewer';
import ClassicView from './components/ClassicView';

type ViewMode = 'classic' | 'advanced';

// Configure PDF worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const ComparisonPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    // State
    const [invoice, setInvoice] = useState<DocumentItem | null>(null);
    const [quote, setQuote] = useState<DocumentItem | null>(null);
    const [loading, setLoading] = useState(true);
    const [rightPanelView, setRightPanelView] = useState<'table' | 'quote'>('table');
    const [viewMode, setViewMode] = useState<ViewMode>('classic');
    const [hoveredEntityType, setHoveredEntityType] = useState<string | null>(null);
    const [focusedEntityType, setFocusedEntityType] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedValues, setEditedValues] = useState<Record<string, string>>({});

    const handleValueChange = useCallback((entityType: string, value: string) => {
        setEditedValues(prev => ({ ...prev, [entityType]: value }));
    }, []);

    const handleSave = useCallback(async () => {
        if (!invoice?.id || Object.keys(editedValues).length === 0) return;
        const fields: Record<string, string | number> = {};
        for (const [entityType, value] of Object.entries(editedValues)) {
            const fieldName = ENTITY_TO_FIELD[entityType];
            if (!fieldName) continue;
            // Coerce numeric fields
            if (['total_amount', 'net_amount', 'total_tax_amount'].includes(fieldName)) {
                const num = parseFloat(value);
                if (!isNaN(num)) fields[fieldName] = num;
            } else {
                fields[fieldName] = value;
            }
        }
        if (Object.keys(fields).length === 0) return;
        try {
            await documentApi.updateDocumentFields(invoice.id, fields);
            setIsEditing(false);
            setEditedValues({});
            fetchData(invoice.id); // Refresh
        } catch (err) {
            console.error('Failed to save:', err);
        }
    }, [invoice?.id, editedValues]);

    const handleCancelEdit = useCallback(() => {
        setIsEditing(false);
        setEditedValues({});
    }, []);

    useEffect(() => {
        if (id) {
            fetchData(id);
        }
    }, [id]);

    const fetchData = async (docId: string) => {
        try {
            setLoading(true);
            const res = await documentApi.getDocument(docId);
            if (res.success && res.data) {
                setInvoice(res.data);

                if (res.data.comparison?.linked_quote_id) {
                    try {
                        const quoteRes = await documentApi.getDocument(res.data.comparison.linked_quote_id);
                        if (quoteRes.success && quoteRes.data) {
                            setQuote(quoteRes.data);
                        }
                    } catch (e) {
                        console.log("Linked quote is likely a system RFQ or not a file document", e);
                    }
                }
            }
        } catch (error) {
            console.error("Failed to fetch document", error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="comparison-loader">Loading...</div>;
    if (!invoice) return <div>Document not found</div>;

    return (
        <div className="comparison-page">
            <header className="comparison-page__header">
                <button className="comparison-page__header-back" onClick={() => navigate(-1)}>
                    ← Back
                </button>
                <div className="comparison-page__header-title">
                    Match Analysis: {invoice.filename}
                </div>
                <div className="comparison-page__header-actions">
                    {/* Classic/Advanced View Toggle */}
                    <div className="comparison-page__view-toggle">
                        <button
                            className={viewMode === 'classic' ? 'active' : ''}
                            onClick={() => setViewMode('classic')}
                        >
                            Classic
                        </button>
                        <button
                            className={viewMode === 'advanced' ? 'active' : ''}
                            onClick={() => setViewMode('advanced')}
                        >
                            Advanced
                        </button>
                    </div>

                    {/* Table/Quote toggle - only show in Advanced mode */}
                    {viewMode === 'advanced' && (
                        <div className="comparison-page__panel-toggle">
                            <button
                                className={rightPanelView === 'table' ? 'active' : ''}
                                onClick={() => setRightPanelView('table')}
                            >
                                Table View
                            </button>
                            <button
                                className={rightPanelView === 'quote' ? 'active' : ''}
                                onClick={() => setRightPanelView('quote')}
                            >
                                Quote PDF
                            </button>
                        </div>
                    )}

                    {/* Edit / Save / Cancel */}
                    {!isEditing ? (
                        <button className="btn btn-secondary" onClick={() => setIsEditing(true)}>Edit</button>
                    ) : (
                        <>
                            <button className="btn btn-primary" onClick={handleSave} disabled={Object.keys(editedValues).length === 0}>Save</button>
                            <button className="btn btn-secondary" onClick={handleCancelEdit}>Cancel</button>
                        </>
                    )}
                    <button className="btn btn-primary" onClick={() => alert("Approved!")}>Approve Match</button>
                </div>
            </header>

            <div className="comparison-page__content">
                {viewMode === 'classic' ? (
                    /* Classic View: Side-by-side PDFs + Table below */
                    <ClassicView
                        invoice={invoice}
                        quote={quote}
                        isEditing={isEditing}
                        editedValues={editedValues}
                        onValueChange={handleValueChange}
                    />
                ) : (
                    /* Advanced View: PDF + Table side by side */
                    <>
                        {/* Left Panel: Invoice PDF with Document AI highlights */}
                        <div className="comparison-page__panel">
                            <DocumentAiPdfViewer
                                document={invoice}
                                title="Invoice"
                                hoveredEntityType={hoveredEntityType}
                                focusedEntityType={focusedEntityType}
                                onClearFocus={() => setFocusedEntityType(null)}
                            />
                        </div>

                        {/* Right Panel: Comparison Table OR Quote PDF */}
                        <div className="comparison-page__panel">
                            <div className="comparison-page__panel-header">
                                {rightPanelView === 'table' ? 'Verification Table' : 'Reference Quote / RFQ'}
                            </div>

                            {rightPanelView === 'table' ? (
                                <ComparisonTable
                                    invoice={invoice}
                                    quote={quote}
                                    onEntityHover={setHoveredEntityType}
                                    onEntityClick={setFocusedEntityType}
                                    isEditing={isEditing}
                                    editedValues={editedValues}
                                    onValueChange={handleValueChange}
                                />
                            ) : (
                                <div className="comparison-page__pdf-container" style={{ background: '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    {quote ? (
                                        <Document
                                            file={`http://localhost:5003/api/documents/${quote.id}/download?token=${localStorage.getItem('auth_token')}`}
                                            loading={<div>Loading Quote PDF...</div>}
                                            onLoadError={(e) => console.error("Quote load error:", e)}
                                        >
                                            <Page pageNumber={1} scale={1.2} />
                                        </Document>
                                    ) : (
                                        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                                            <p style={{ fontSize: '1.1rem', fontWeight: 600 }}>No Quote PDF available</p>
                                            <p style={{ fontSize: '0.9rem', marginTop: 10 }}>Linked Quote ID: {invoice?.comparison?.linked_quote_id || 'None'}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ComparisonPage;
